/**
 * Date utility functions for formatting, parsing, and validating dates
 */

/**
 * Format a Date object as MM/DD/YYYY
 * @param date - The date to format, or null
 * @returns Formatted date string or empty string if date is null
 */
export const formatDate = (date: Date | null): string => {
  if (!date) return "";
  
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();
  
  return `${month}/${day}/${year}`;
};

/**
 * Check if a date is valid
 * @param month - Month (1-12)
 * @param day - Day of month
 * @param year - Year (4 digits or 2 digits)
 * @returns Whether the date is valid
 */
export const isValidDate = (month: number, day: number, year: number): boolean => {
  // Basic range checks
  if (month < 1 || month > 12) return false;
  if (day < 1 || day > 31) return false;
  
  // Check for specific month lengths
  const monthLengths = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  
  // Adjust February for leap years
  // For 2-digit years, we need to handle them specially
  let isLeapYear = false;
  if (year < 100) {
    // For 2-digit years, we'll consider them valid if they would be valid
    // in either the 1900s or 2000s
    isLeapYear = 
      ((year + 1900) % 4 === 0 && (year + 1900) % 100 !== 0) || (year + 1900) % 400 === 0 ||
      ((year + 2000) % 4 === 0 && (year + 2000) % 100 !== 0) || (year + 2000) % 400 === 0;
  } else {
    isLeapYear = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
  }
  
  if (isLeapYear) {
    monthLengths[1] = 29;
  }
  
  // Check if the day is valid for the given month
  return day <= monthLengths[month - 1];
};

/**
 * Regular expressions for date formats
 */
export const dateRegex = {
  fullYear: /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/,  // Exactly 4 digits for year
  shortYear: /^(\d{1,2})\/(\d{1,2})\/(\d{2})$/,  // Exactly 2 digits for year
  partialYear: /^(\d{1,2})\/(\d{1,2})\/(\d{1,3})$/,  // 1-3 digits for partial years during backspacing
  partialDate: /^(\d{1,2})\/(\d{1,2})(\/(\d{0,3}))?$/,  // For any partial date
  invalidChars: /[^\d\/]/,
  // Add a pattern to detect invalid year lengths (more than 4 digits)
  invalidYearLength: /^(\d{1,2})\/(\d{1,2})\/(\d{5,})$/
};

/**
 * Parse a date string to a Date object
 * @param dateString - Date string in MM/DD/YYYY or MM/DD/YY format
 * @param preserveFormat - Whether to preserve the original format (don't convert 2-digit years)
 * @param allowPartial - Whether to allow partial inputs (for backspacing)
 * @returns Parsed Date object or null if invalid
 */
export const parseDate = (dateString: string, preserveFormat = false, allowPartial = false): Date | null => {
  // Check for invalid year lengths (more than 4 digits)
  if (dateRegex.invalidYearLength.test(dateString)) {
    return null;
  }
  
  // Check if this is a partial input (like when backspacing)
  // This includes any input that doesn't match the complete date formats
  // or specifically matches our partial year pattern
  const isPartialInput = !dateRegex.fullYear.test(dateString) && 
                         !dateRegex.shortYear.test(dateString);
  
  // If we're allowing partial inputs and this is a partial input,
  // return null immediately to prevent any auto-correction
  if (allowPartial && isPartialInput) {
    return null;
  }
  
  // Check specifically for partial years like "03/16/202"
  const partialYearMatch = dateString.match(dateRegex.partialYear);
  if (partialYearMatch && !dateRegex.fullYear.test(dateString) && !dateRegex.shortYear.test(dateString)) {
    // Always return null for partial years to prevent auto-correction
    return null;
  }
  
  // Check if this is a two-digit year format (e.g., "01/01/23")
  // If preserveFormat is true, we want to keep it as a two-digit year
  // If allowPartial is true, we also want to return null to prevent auto-correction
  const isTwoDigitYear = dateRegex.shortYear.test(dateString);
  if (isTwoDigitYear && allowPartial) {
    // Return null for two-digit years when allowPartial is true
    // This prevents auto-correction during typing
    return null;
  }
  
  // First, try the MM/DD/YYYY format
  const fullYearMatch = dateString.match(dateRegex.fullYear);
  
  if (fullYearMatch) {
    const month = parseInt(fullYearMatch[1], 10);
    const day = parseInt(fullYearMatch[2], 10);
    const year = parseInt(fullYearMatch[3], 10);
    
    // Check if the date is valid (e.g., not 02/30/2023)
    if (isValidDate(month, day, year)) {
      return new Date(year, month - 1, day);
    }
  }
  
  // Next, try the MM/DD/YY format
  const shortYearMatch = dateString.match(dateRegex.shortYear);
  
  if (shortYearMatch) {
    const month = parseInt(shortYearMatch[1], 10);
    const day = parseInt(shortYearMatch[2], 10);
    let year = parseInt(shortYearMatch[3], 10);
    
    // If preserveFormat is true, we need to create a date with the 2-digit year
    if (preserveFormat) {
      // We need to create a date in the range 0-99 AD
      // JavaScript dates work with years 0-99 as actual years, not 1900+year
      if (isValidDate(month, day, year)) {
        const date = new Date(0, 0, 1); // Create a base date
        date.setFullYear(year); // Set the year (0-99)
        date.setMonth(month - 1);
        date.setDate(day);
        return date;
      }
    } else {
      // Convert 2-digit year to 4-digit year
      // If year is less than 50, assume it's 20xx, otherwise 19xx
      year = year < 50 ? 2000 + year : 1900 + year;
      
      // Check if the date is valid
      if (isValidDate(month, day, year)) {
        return new Date(year, month - 1, day);
      }
    }
  }
  
  return null;
}; 